// Lógica pura de espacios (salones/jardines) por venue — FRA-293.
//
// Modelo (subcolección venues/{venueId}/espacios/{espacioId}):
// - El doc ID es el slug y es INMUTABLE después de crear: es la URL pública
//   indexable y la clave que los eventos congelan en `unidadesOcupadas`.
//   Un slug mal escrito se corrige borrando y recreando el espacio.
// - `esAtomico` ausente ⇒ true. Un compuesto lista sus partes en
//   `componentes`; la resolución tolera anidamiento arbitrario, ciclos
//   (termina y devuelve lo alcanzable) y referencias a espacios borrados
//   (se ignoran).
// - `incompatibleCon` se declara SOLO entre átomos y se guarda simétrica en
//   ambos lados. Los compuestos la heredan por contención (la unión de la de
//   sus átomos), nunca llevan arista propia.
// - En un evento, `unidadesOcupadas` ausente o vacío ⇒ ocupa TODO el venue.
//   Es el default seguro que mantiene compatibles los eventos legacy; nunca
//   se interpreta como "no ocupa nada".
//
// Espejo en Python: backend/src/espacios.py — no hay código compartido entre
// subproyectos, los dos lados deben mantenerse alineados.

const esAtomico = (esp) => esp.esAtomico !== false;

// Footprint de un espacio: el conjunto de unidades atómicas que ocupa.
// Para un atómico es {su propio id}.
export function resolverFootprint(espacioId, espaciosPorId) {
  const visitados = new Set();
  const resultado = new Set();
  const pila = [espacioId];
  while (pila.length) {
    const id = pila.pop();
    if (visitados.has(id)) continue; // guard de ciclos
    visitados.add(id);
    const esp = espaciosPorId[id];
    if (!esp) continue; // espacio borrado — se ignora
    if (esAtomico(esp)) resultado.add(id);
    else (esp.componentes || []).forEach((c) => pila.push(c));
  }
  return resultado;
}

// Footprint de un evento multi-espacio: la unión de los footprints.
export function resolverFootprintMulti(espacioIds, espaciosPorId) {
  const resultado = new Set();
  (espacioIds || []).forEach((id) => {
    resolverFootprint(id, espaciosPorId).forEach((a) => resultado.add(a));
  });
  return resultado;
}

// ¿Existe a ∈ A, b ∈ B tal que b ∈ incompatibleCon(a)? Como la arista se
// guarda simétrica, basta recorrer un lado.
export function sonIncompatibles(footprintA, footprintB, espaciosPorId) {
  for (const a of footprintA) {
    const esp = espaciosPorId[a];
    const incompatibles = (esp && esp.incompatibleCon) || [];
    for (const b of incompatibles) {
      if (footprintB.has(b)) return true;
    }
  }
  return false;
}

// ── Footprints de eventos (FRA-295) ─────────────────────────────────
// El calendario y las superficies públicas trabajan con el rollup
// (espaciosResumen), que ya trae `unidades` e `incompatibleCon` resueltos —
// nunca con la subcolección.

export function resumenPorId(espaciosResumen) {
  const map = {};
  (espaciosResumen || []).forEach((e) => {
    map[e.id] = e;
  });
  return map;
}

// Footprint congelado de un evento ya guardado. Ausente o vacío ⇒ null, el
// sentinel de "todo el venue" (default seguro de los eventos legacy). Nunca
// se interpreta como "no ocupa nada".
export function footprintDeEvento(ev) {
  const unidades =
    (ev && ev.unidadesOcupadas) ||
    (ev && ev.extendedProps && ev.extendedProps.unidadesOcupadas);
  return Array.isArray(unidades) && unidades.length > 0
    ? new Set(unidades)
    : null;
}

// Footprint de una selección de espacios vía el rollup. Selección vacía —o
// que solo apunta a espacios ya borrados— degrada a null (todo el venue).
export function footprintDesdeResumen(espaciosIds, porId) {
  const out = new Set();
  (espaciosIds || []).forEach((id) => {
    const entry = porId[id];
    ((entry && entry.unidades) || []).forEach((u) => out.add(u));
  });
  return out.size > 0 ? out : null;
}

// Conflicto entre dos footprints (null = todo el venue): contención por
// intersección o incompatibilidad declarada entre sus átomos.
export function hayConflictoEntreFootprints(fpA, fpB, espaciosPorId) {
  if (!fpA || !fpB) return true;
  if (fpA.size === 0 || fpB.size === 0) return true;
  for (const u of fpA) if (fpB.has(u)) return true;
  return sonIncompatibles(fpA, fpB, espaciosPorId);
}

// ¿El evento bloquea este espacio (entrada del rollup)? Fórmula del epic:
// fp(e) ∩ X.unidades ≠ ∅  ∨  fp(e) ∩ X.incompatibleCon ≠ ∅.
export function eventoBloqueaEspacio(fpEvento, espacioResumen) {
  if (!fpEvento) return true;
  const unidades =
    espacioResumen.unidades && espacioResumen.unidades.length
      ? espacioResumen.unidades
      : [espacioResumen.id];
  if (unidades.some((u) => fpEvento.has(u))) return true;
  return (espacioResumen.incompatibleCon || []).some((u) => fpEvento.has(u));
}

// Cómo se relaciona un evento con un espacio. `eventoBloqueaEspacio` responde
// "¿se puede vender?" (sí/no), que es lo correcto para conflictos y
// disponibilidad — pero pintar los dos casos igual MIENTE: un evento en Salón
// C + Jardín A deja los cuatro espacios sin vender (Salón A contiene a C,
// Salón B es incompatible con Jardín A) y se ve como si el evento ocurriera
// en los cuatro.
//
//   "directo"      — el evento ESTÁ ahí: lo eligió el owner, o consume el
//                    footprint completo del espacio (Salón A cuando el evento
//                    tomó B y C), o es legacy y ocupa todo el venue.
//   "consecuencia" — el evento NO está ahí, pero lo deja sin vender: contención
//                    parcial o incompatibilidad declarada.
//   null           — libre.
export function relacionEventoEspacio(espaciosIds, fpEvento, espacioResumen) {
  if (!eventoBloqueaEspacio(fpEvento, espacioResumen)) return null;
  // Sin footprint el evento ocupa el venue entero: está en todos lados.
  if (!fpEvento) return "directo";
  if ((espaciosIds || []).includes(espacioResumen.id)) return "directo";
  const unidades =
    espacioResumen.unidades && espacioResumen.unidades.length
      ? espacioResumen.unidades
      : [espacioResumen.id];
  if (unidades.every((u) => fpEvento.has(u))) return "directo";
  return "consecuencia";
}

// Qué espacios reservables quedan bloqueados por un conjunto de eventos que ya
// cruzan el rango de interés. `bloqueadores` son { fp, ref } donde `fp` es el
// footprint del evento (null = todo el venue) y `ref` lo que el consumidor
// quiera adjuntar para atribuir el bloqueo (el evento, su nombre, su id).
// Devuelve Map(espacioId → [ref…]) solo con los espacios bloqueados.
//
// Es la pregunta que hace un LEAD ("¿me queda algo libre esa fecha?"), no la
// que hace un evento al guardarse. Ojo con la asimetría: en un evento la
// ausencia de footprint significa "ocupa todo"; en un lead la ausencia de
// selección significa "le sirve cualquier espacio", y eso se resuelve
// comparando este mapa contra la lista de espacios reservables.
export function bloqueosPorEspacio(bloqueadores, espaciosActivos) {
  const map = new Map();
  (espaciosActivos || []).forEach((esp) => {
    const refs = (bloqueadores || [])
      .filter((b) => eventoBloqueaEspacio(b.fp, esp))
      .map((b) => b.ref);
    if (refs.length > 0) map.set(esp.id, refs);
  });
  return map;
}

// ── Migración de eventos legacy ─────────────────────────────────────
// Cuando un venue enciende multiespacio, TODOS sus eventos anteriores quedan
// sin footprint y, por el contrato de arriba, cada uno bloquea el venue
// entero: el calendario se vuelve un muro y la disponibilidad pública se
// apaga. No se pueden asignar solos —adivinar liberaría fechas y provocaría
// un empalme— así que hay que marcarlos y que un humano decida.
//
// `ocupaTodoElVenue: true` es una marca SOLO DE UI: no cambia el cálculo de
// conflictos (la ausencia de footprint sigue mandando), únicamente registra
// que alguien ya revisó ese evento y confirmó que sí toma todo. Sin ella no
// habría forma de distinguir "nadie lo ha visto" de "sí, es el venue entero",
// y el aviso no se apagaría nunca.
export function eventoSinEspacioAsignado(ev, { espaciosActivos = [], permiteEventosSimultaneos = true, ahora = new Date() } = {}) {
  // Solo importa donde reservar por salón significa algo.
  if (espaciosActivos.length < 2 || !permiteEventosSimultaneos) return false;
  if (!ev || ev.status === "cancelado") return false;
  if (ev.ocupaTodoElVenue === true) return false; // revisado y confirmado
  if (footprintDeEvento(ev)) return false;        // ya tiene footprint
  // Un evento pasado ya no bloquea nada vendible: no vale la pena molestar.
  const fin = ev.endTime || ev.startTime;
  const d = fin && typeof fin.toDate === "function" ? fin.toDate() : fin ? new Date(fin) : null;
  if (!d || isNaN(d)) return false;
  return d >= ahora;
}

// Rollup denormalizado para el doc del venue. Puro: recibe la lista completa
// de espacios ({ id, ...data }) y devuelve los campos a escribir tal cual.
// Quien escribe (el CRUD del app, issue FRA-294) lo recalcula en cada
// alta/baja/edición — no hay trigger que lo mantenga.
export function calcularRollup(espacios) {
  const porId = {};
  espacios.forEach((e) => {
    porId[e.id] = e;
  });

  const espaciosResumen = [...espacios]
    .sort(
      (a, b) =>
        (a.orden ?? 0) - (b.orden ?? 0) ||
        (a.nombre || "").localeCompare(b.nombre || "")
    )
    .map((e) => {
      const unidades = [...resolverFootprint(e.id, porId)].sort();
      // Incompatibilidad expandida a átomos; en compuestos, heredada (la
      // unión de la de sus átomos). Nunca contra el propio footprint.
      const heredada = new Set();
      unidades.forEach((a) =>
        ((porId[a] && porId[a].incompatibleCon) || []).forEach((x) =>
          heredada.add(x)
        )
      );
      unidades.forEach((a) => heredada.delete(a));
      return {
        id: e.id,
        nombre: e.nombre || "",
        slug: e.slug || e.id,
        sentadosMax: e.capacidad?.sentadosMax ?? null,
        piePMax: e.capacidad?.piePMax ?? null,
        superficieM2: e.superficieM2 ?? null,
        activo: e.activo !== false,
        // reservable: false = área de showcase (cocina, suite) — sale en el
        // tab público de espacios pero NUNCA en calendario/conflictos.
        reservable: e.reservable !== false,
        unidades,
        incompatibleCon: [...heredada].sort(),
      };
    });

  // Solo los reservables definen la capacidad rentable del venue — la
  // capacidad de una cocina no es capacidad de renta.
  const activos = espacios.filter(
    (e) => e.activo !== false && e.reservable !== false
  );
  const numeros = (vals) =>
    vals.filter((v) => typeof v === "number" && !isNaN(v));
  const minimos = numeros(
    activos.flatMap((e) => [e.capacidad?.sentadosMin, e.capacidad?.piePMin])
  );
  const maximos = numeros(
    activos.flatMap((e) => [e.capacidad?.sentadosMax, e.capacidad?.piePMax])
  );

  return {
    espaciosResumen,
    capacidadMin: minimos.length ? Math.min(...minimos) : null,
    capacidadMax: maximos.length ? Math.max(...maximos) : null,
    espaciosCount: espacios.length,
  };
}
